import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import componentUtils from '../../lib/componentUtils';
import SideDrawerHeader from './SideDrawerHeader';
import SideDrawerFooter from './SideDrawerFooter';

import './SideDrawer.css';

interface Extra {
  width?: string;
}
interface BaseSideDrawerProps extends Extra {
  className?: string;
  children: {
    header?: React.ComponentType<{ handleClose?: () => void } & Extra>;
    content: React.ReactNode;
    footer?: React.ComponentType<{ handleClose?: () => void } & Extra>;
  };
  displayDrawer?: boolean;

  onClose: () => void;
  backgroundRef?: React.RefObject<HTMLDivElement>;
  bodyRef?: React.RefObject<HTMLDivElement>;
}

export const BaseSideDrawer = ({
  children,
  className,
  onClose,
  backgroundRef,
  displayDrawer,
  width,
  bodyRef,
}: BaseSideDrawerProps): JSX.Element => {
  const [drawerClosing, setDrawerClosing] = useState(false);
  const HeaderComponent = children.header;
  const FooterComponent = children.footer;

  const handleClose = useCallback(() => {
    setDrawerClosing(false);
    onClose();
  }, [onClose]);

  const style: React.CSSProperties | undefined = width ? { width } : undefined;

  return (
    <div className={classNames('side-drawer__container', className)}>
      <div
        className={classNames({ 'side-drawer__background-overlay': displayDrawer })}
        onClick={handleClose}
        ref={backgroundRef}
      />
      <div
        className={classNames(
          'side-drawer__body',
          { 'side-drawer__closing': !displayDrawer && drawerClosing },
          { 'side-drawer__opening': displayDrawer }
        )}
        style={style}
        ref={bodyRef}
      >
        {HeaderComponent ? <HeaderComponent handleClose={handleClose} width={width} /> : null}
        {children.content}
        {FooterComponent ? <FooterComponent handleClose={handleClose} width={width} /> : null}
      </div>
    </div>
  );
};

interface SideDrawerProps extends Omit<BaseSideDrawerProps, 'children'> {
  header?: string;
  cta?: string;
  ctaOnClick?: () => void;
  headerContent?: React.ReactNode;
  children: React.ReactNode;
  contentRef?: React.RefObject<HTMLDivElement>;
}

/** @deprecated - use SidedrawerV2 */
const SideDrawer = (props: SideDrawerProps): JSX.Element => {
  const { header, width, children, cta, ctaOnClick, headerContent, contentRef, className } = props;
  const hasChildren = componentUtils.hasChildren(children);

  const style: React.CSSProperties | undefined = width ? { width } : undefined;

  return (
    <BaseSideDrawer {...props} className={classNames('zola-ui', className)}>
      {{
        header: ({ handleClose }) => (
          <SideDrawerHeader handleClose={handleClose} header={header} style={style}>
            {headerContent}
          </SideDrawerHeader>
        ),
        content: (
          <div className="side-drawer__content" style={style} ref={contentRef}>
            {hasChildren && children}
          </div>
        ),
        footer: () =>
          cta ? <SideDrawerFooter cta={cta} ctaOnClick={ctaOnClick} style={style} /> : null,
      }}
    </BaseSideDrawer>
  );
};

export default SideDrawer;
